import { BrandSymbol, BrandSymbolKey, brandSymbolsMap, Icon, IconKey, NakedButton } from '@melio/penny';
import {
  BankFundingSourceTypeOptionServer,
  Card,
  CardFundingSourceTypeOptionServer,
  FundingSource,
  FundingSourceType,
} from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';

export const getFundingSourceImageProps = (fundingSource: FundingSource) => {
  if (fundingSource.type === 'bank-account' && fundingSource.logo && !fundingSource.logo.includes('default')) {
    return { src: fundingSource.logo, alt: 'Account provider logo' };
  }
  return;
};

export const getFundingSourceIconByNetwork = (network: Card['network']): IconKey | BrandSymbolKey => {
  switch (network) {
    case 'visa':
      return 'visa';
    case 'amex':
      return 'amex-logo';
    case 'mastercard':
      return 'mastercard';
    case 'discover':
      return 'discover';
    default:
      return 'credit-card';
  }
};

export type FundingSourceTypeOptionReasonClient = 'goodNotReceived';

export type UnsupportedFundingSourceTypeReasons =
  | CardFundingSourceTypeOptionServer['reason']
  | FundingSourceTypeOptionReasonClient;

export type CardFundingSourceTypeOption = Omit<CardFundingSourceTypeOptionServer, 'reason'> & {
  reason?: UnsupportedFundingSourceTypeReasons;
};

export type BankFundingSourceTypeOption = Omit<BankFundingSourceTypeOptionServer, 'reason'> & {
  reason?: UnsupportedFundingSourceTypeReasons;
};

export type FundingSourceTypesOption = StrictUnion<BankFundingSourceTypeOption | CardFundingSourceTypeOption>;

export const fsTypesOptionsToFundingSourceTypesOptions = (
  fsTypeOption: BankFundingSourceTypeOptionServer | CardFundingSourceTypeOptionServer
): FundingSourceTypesOption => {
  switch (fsTypeOption.type) {
    case FundingSourceType.BankAccount:
      return fsTypeOption as BankFundingSourceTypeOption;
    case FundingSourceType.Card:
    default:
      return fsTypeOption as CardFundingSourceTypeOption;
  }
};

const getFundingSourceOption = (
  fundingSource: RecursivePartial<FundingSource>,
  fundingSourceTypesOptions?: FundingSourceTypesOption[]
) => {
  const { type, details } = fundingSource;
  if (!type || !fundingSourceTypesOptions) {
    return undefined;
  }

  const network = type === 'card' ? details?.network : undefined;
  return network
    ? fundingSourceTypesOptions.find((it) => it.type === type && it.subtype === network)
    : fundingSourceTypesOptions.find((it) => it.type === type);
};

export const isFundingSourceDisabled = (
  fundingSource: RecursivePartial<FundingSource>,
  fundingSourceTypesOptions?: FundingSourceTypesOption[]
) => !getFundingSourceOption(fundingSource, fundingSourceTypesOptions)?.supported;

export const getDisabledFundingSourceReason = (
  fundingSource: RecursivePartial<FundingSource>,
  fundingSourceTypesOptions?: FundingSourceTypesOption[]
): UnsupportedFundingSourceTypeReasons => getFundingSourceOption(fundingSource, fundingSourceTypesOptions)?.reason;

export const getDisabledFundingTypeReasons = (
  fundingType: RecursivePartial<FundingSourceType>,
  fundingSourceTypesOptions?: FundingSourceTypesOption[]
): UnsupportedFundingSourceTypeReasons[] | undefined => {
  const options = fundingSourceTypesOptions?.filter((it) => it.type === fundingType);
  if (!options || options.some((it) => it.supported)) {
    return undefined;
  }

  return [...new Set(options?.map((it) => it.reason))];
};

export const isFundingTypeSupported = (
  fundingType: FundingSourceType,
  fundingSourceTypesOptions: FundingSourceTypesOption[]
) => !!fundingSourceTypesOptions.find((it) => it.type === fundingType && it.supported);

export const getFundingSourceIconByType = ({
  fundingType,
  bankIcon,
  cardType,
}: {
  fundingType: FundingSource['type'];
  bankIcon: IconKey | BrandSymbolKey;
  cardType?: Card['type'];
}): IconKey | BrandSymbolKey => {
  switch (fundingType) {
    case 'bank-account':
      return bankIcon;
    case 'card': {
      if (cardType == 'debit') {
        return 'debit-card';
      } else {
        return 'credit-card';
      }
    }
  }
};

export const getFundingSourceIconType = (fundingSource: FundingSource): IconKey | BrandSymbolKey => {
  if (fundingSource.type === 'bank-account') {
    return 'bank';
  }
  if (fundingSource.details.network) {
    return getFundingSourceIconByNetwork(fundingSource.details.network);
  }
  return fundingSource.details.type == 'debit' ? 'debit-card' : 'credit-card';
};

export const useGetFundingSourceLabel = (fundingSource?: FundingSource) => {
  const { formatMessage } = useMelioIntl();

  if (!fundingSource) {
    return '';
  }

  switch (fundingSource.type) {
    case FundingSourceType.BankAccount:
      return formatMessage('utils.paymentSource.bankAccount.label');
    case FundingSourceType.Card:
      return formatMessage(`utils.paymentSource.${fundingSource.details.type}.label`);
  }
};

export const useGetFundingSourceCardHelperText = ({
  fundingSource,
  fee,
  onClick,
}: {
  fundingSource?: FundingSource;
  fee?: string | JSX.Element;
  onClick?: VoidFunction;
}) => {
  const { formatMessage } = useMelioIntl();

  if (!fundingSource) {
    return '';
  }

  const { type, isVerified, isBlocked } = fundingSource;

  if (type === 'bank-account') {
    if (isVerified) {
      return fee
        ? formatMessage('widgets.paymentMethods.paymentMethodLineItem.bankAccount.verified.helperText', { fee })
        : undefined;
    } else {
      if (isBlocked) {
        return formatMessage('widgets.paymentMethods.paymentMethodLineItem.bankAccount.blocked.helperText');
      }
      return formatMessage('widgets.paymentMethods.paymentMethodLineItem.bankAccount.unverified.helperText', {
        link: (
          <NakedButton
            variant="secondary"
            onClick={onClick}
            size="medium"
            label={formatMessage('widgets.paymentMethods.paymentMethodLineItem.bankAccount.unverified.link')}
          />
        ),
      });
    }
  } else if (fundingSource.details?.type == 'credit') {
    return formatMessage('widgets.paymentMethods.paymentMethodLineItem.credit.helperText', { fee });
  } else if (fundingSource.details?.type == 'debit') {
    return formatMessage('widgets.paymentMethods.paymentMethodLineItem.debit.helperText', { fee });
  }

  return `Missing Description for fundingType: ${fundingSource.type}`;
};

export const getFundingSourceIconOrBrandSymbol = (fundingSource: FundingSource) => {
  const iconType = getFundingSourceIconType(fundingSource);

  // TODO: replace with utility from @melio/platform-api - https://meliorisk.atlassian.net/browse/ME-24864
  const isBrandSymbol = Object.keys(brandSymbolsMap).includes(iconType as BrandSymbolKey);

  return isBrandSymbol ? (
    <BrandSymbol type={iconType as BrandSymbolKey} />
  ) : (
    <Icon type={iconType as IconKey} color="inherit" />
  );
};
